import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Shield, FileText, Lock, Users } from 'lucide-react';

const Legal: React.FC = () => {
  const { t } = useTranslation();

  const legalSectionsData = t('legal.sections', { returnObjects: true }) as Array<{
    title: string;
    content: string;
  }>;
  
  const icons = [Shield, Lock, FileText, Users];
  
  const legalSections = legalSectionsData.map((section, index) => ({
    ...section,
    icon: icons[index]
  })) as Array<{
    title: string;
    content: string;
    icon: React.ComponentType<{ className?: string }>;
  }>;

  return (
    <>
      <Helmet>
        <title>{t('meta.legal.title')}</title>
        <meta 
          name="description" 
          content={t('meta.legal.description')} 
        />
        <link rel="canonical" href={t('meta.legal.canonical')} />
      </Helmet>

      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-primary-50">
        <div className="container-max">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-serif font-bold text-primary-800 mb-6">
              {t('legal.title')}
            </h1>
            <p className="text-xl text-gray-600">
              {t('legal.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Legal Sections */}
      <section className="section-padding bg-white">
        <div className="container-max">
          <div className="max-w-4xl mx-auto">
            <div className="space-y-12">
              {legalSections.map((section, index) => (
                <div key={index} className="border-l-4 border-primary-600 pl-6">
                  <div className="flex items-start space-x-4 rtl:space-x-reverse">
                    <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center flex-shrink-0">
                      <section.icon className="w-6 h-6 text-primary-600" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-serif font-bold text-primary-800 mb-4">
                        {section.title}
                      </h2>
                      <p className="text-gray-600 leading-relaxed">
                        {section.content}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Detailed Legal Information */}
      <section className="section-padding bg-primary-50">
        <div className="container-max">
          <div className="max-w-4xl mx-auto">
            <div className="space-y-8">
              <div className="bg-white rounded-lg p-8 shadow-sm">
                <h2 className="text-2xl font-serif font-bold text-primary-800 mb-6">
                  {t('legal.detailedSections.termsOfService.title')}
                </h2>
                <div className="space-y-4 text-gray-600">
                  {(t('legal.detailedSections.termsOfService.content', { returnObjects: true }) as string[]).map((paragraph, index) => (
                    <p key={index}>
                      {paragraph}
                    </p>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-lg p-8 shadow-sm">
                <h2 className="text-2xl font-serif font-bold text-primary-800 mb-6">
                  {t('legal.detailedSections.privacyPolicy.title')}
                </h2>
                <div className="space-y-4 text-gray-600">
                  {(t('legal.detailedSections.privacyPolicy.content', { returnObjects: true }) as string[]).map((paragraph, index) => (
                    <p key={index}>
                      {paragraph}
                    </p>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-lg p-8 shadow-sm">
                <h2 className="text-2xl font-serif font-bold text-primary-800 mb-6">
                  {t('legal.detailedSections.internationalShipping.title')}
                </h2>
                <div className="space-y-4 text-gray-600">
                  {(t('legal.detailedSections.internationalShipping.content', { returnObjects: true }) as string[]).map((paragraph, index) => (
                    <p key={index}>
                      {paragraph}
                    </p>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-lg p-8 shadow-sm">
                <h2 className="text-2xl font-serif font-bold text-primary-800 mb-6">
                  {t('legal.detailedSections.customerRights.title')}
                </h2>
                <div className="space-y-4 text-gray-600">
                  {(t('legal.detailedSections.customerRights.content', { returnObjects: true }) as string[]).map((paragraph, index) => (
                    <p key={index}>
                      {paragraph}
                    </p>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact for Legal Questions */}
      <section className="section-padding bg-white">
        <div className="container-max">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-serif font-bold text-primary-800 mb-6">
              {t('legal.questionsTitle')}
            </h2>
            <p className="text-lg text-gray-600 mb-8">
              {t('legal.questionsSubtitle')}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/contact"
                className="btn-primary inline-flex items-center justify-center"
              >
{t('legal.contactUs')}
              </a>
              <a
                href={t('common.contact.legalEmail')}
                className="btn-secondary inline-flex items-center justify-center"
              >
{t('legal.emailLegalTeam')}
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Legal;
